import { useTranslation } from '@blocs.i18n';
import { FiltersNameEnum } from '@blocs.filter';
import { useFeatureExperiment, FEATURE_FLAGS } from '@blocs.features-experiments';
import { FilterDrawerType, FiltersCategoryNameEnum, FiltersSectionNameEnum } from '../types';
import { useCastingFiltersLabel, useRepsFiltersLabel, useTalentScoutFiltersLabel } from './useFiltersLabel';

type Option = {
  value: string;
  label: string;
  options: Array<Option>;
};

export const useDrawerNavigationSections = (type: FilterDrawerType): Array<Option> => {
  const { t } = useTranslation();
  const castingFiltersLabel = useCastingFiltersLabel();
  const repsFiltersLabel = useRepsFiltersLabel();
  const talentScoutFiltersLabel = useTalentScoutFiltersLabel();

  const isPrimaryWorkingLocationEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_CASTING_PRIMARY_WORKING_FILTER);

  const workingLocationsFilter = {
    value: FiltersCategoryNameEnum.workingLocations,
    label: t('common:filtersName.workingLocations'),
    options: [
      {
        value: FiltersNameEnum.workingLocation,
        label: castingFiltersLabel[FiltersNameEnum.workingLocation],
      },
    ],
  };

  const drawers = {
    [FilterDrawerType.Casting]: [
      {
        value: FiltersSectionNameEnum.topFilters,
        label: t('common:filtersName.topFilters'),
        options: [
          {
            value: FiltersCategoryNameEnum.representation,
            label: t('common:filtersName.representation'),
            options: [
              { value: FiltersNameEnum.talentType, label: castingFiltersLabel[FiltersNameEnum.talentType] },
              {
                value: FiltersNameEnum.representatives,
                label: castingFiltersLabel[FiltersNameEnum.representatives],
              },
              {
                value: FiltersNameEnum.representativeList,
                label: castingFiltersLabel[FiltersNameEnum.representativeList],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.unionStatus,
            label: castingFiltersLabel[FiltersNameEnum.unionStatus],
            options: [
              {
                value: FiltersNameEnum.unionStatus,
                label: castingFiltersLabel[FiltersNameEnum.unionStatus],
              },
              {
                value: FiltersNameEnum.excludeUnionStatus,
                label: castingFiltersLabel[FiltersNameEnum.unionStatus],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.genderAppearance,
            label: t('common:form.title.genderAppearance'),
            options: [
              { value: FiltersNameEnum.gender, label: castingFiltersLabel[FiltersNameEnum.gender] },
              {
                value: FiltersNameEnum.additionallyIdentifiesAs,
                label: castingFiltersLabel[FiltersNameEnum.additionallyIdentifiesAs],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.ethnicAppearance,
            label: castingFiltersLabel[FiltersNameEnum.ethnicAppearance],
            options: [
              {
                value: FiltersNameEnum.ethnicAppearance,
                label: castingFiltersLabel[FiltersNameEnum.additionallyIdentifiesAs],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.age,
            label: t('common:filtersName.age'),
            options: [
              {
                value: FiltersNameEnum.playableAgeRange,
                label: castingFiltersLabel[FiltersNameEnum.playableAgeRange],
              },
              { value: FiltersNameEnum.minorsAgeRange, label: castingFiltersLabel[FiltersNameEnum.minorsAgeRange] },
            ],
          },
          ...(isPrimaryWorkingLocationEnabled ? [] : [workingLocationsFilter]),
          {
            value: FiltersCategoryNameEnum.heightAndWeight,
            label: t('common:form.title.heightAndWeight'),
            options: [
              { value: FiltersNameEnum.heightRange, label: castingFiltersLabel[FiltersNameEnum.heightRange] },
              { value: FiltersNameEnum.weightRange, label: castingFiltersLabel[FiltersNameEnum.weightRange] },
            ],
          },
        ],
      },
      {
        value: FiltersSectionNameEnum.additionalFilters,
        label: t('common:filtersName.additionalFilters'),
        options: [
          {
            value: FiltersCategoryNameEnum.assets,
            label: t('common:form.title.assets'),
            options: [
              { value: FiltersNameEnum.dogBreed, label: castingFiltersLabel[FiltersNameEnum.dogBreed] },
              { value: FiltersNameEnum.dogBreedSize, label: castingFiltersLabel[FiltersNameEnum.dogBreedSize] },
              { value: FiltersNameEnum.otherAnimals, label: castingFiltersLabel[FiltersNameEnum.otherAnimals] },
              { value: FiltersNameEnum.props, label: castingFiltersLabel[FiltersNameEnum.props] },
              {
                value: FiltersNameEnum.sportsEquipment,
                label: castingFiltersLabel[FiltersNameEnum.sportsEquipment],
              },
              { value: FiltersNameEnum.wardrobe, label: castingFiltersLabel[FiltersNameEnum.wardrobe] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.clothingSizes,
            label: t('common:profile.sizes.clothingSizes'),
            options: [
              { value: FiltersNameEnum.sizesCategory, label: castingFiltersLabel[FiltersNameEnum.sizesCategory] },
              {
                value: FiltersNameEnum.sizesChildrenCategory,
                label: castingFiltersLabel[FiltersNameEnum.sizesChildrenCategory],
              },
              { value: FiltersNameEnum.chest, label: castingFiltersLabel[FiltersNameEnum.chest] },
              { value: FiltersNameEnum.neck, label: castingFiltersLabel[FiltersNameEnum.neck] },
              { value: FiltersNameEnum.sleeve, label: castingFiltersLabel[FiltersNameEnum.sleeve] },
              { value: FiltersNameEnum.coat, label: castingFiltersLabel[FiltersNameEnum.coat] },
              { value: FiltersNameEnum.waist, label: castingFiltersLabel[FiltersNameEnum.waist] },
              { value: FiltersNameEnum.inseam, label: castingFiltersLabel[FiltersNameEnum.inseam] },
              { value: FiltersNameEnum.glove, label: castingFiltersLabel[FiltersNameEnum.glove] },
              { value: FiltersNameEnum.hat, label: castingFiltersLabel[FiltersNameEnum.hat] },
              { value: FiltersNameEnum.menTShirt, label: castingFiltersLabel[FiltersNameEnum.menTShirt] },
              { value: FiltersNameEnum.menShoes, label: castingFiltersLabel[FiltersNameEnum.menShoes] },
              { value: FiltersNameEnum.menShoeWidth, label: castingFiltersLabel[FiltersNameEnum.menShoeWidth] },
              { value: FiltersNameEnum.dress, label: castingFiltersLabel[FiltersNameEnum.dress] },
              { value: FiltersNameEnum.bust, label: castingFiltersLabel[FiltersNameEnum.bust] },
              { value: FiltersNameEnum.underbust, label: castingFiltersLabel[FiltersNameEnum.underbust] },
              { value: FiltersNameEnum.cup, label: castingFiltersLabel[FiltersNameEnum.cup] },
              { value: FiltersNameEnum.hip, label: castingFiltersLabel[FiltersNameEnum.hip] },
              { value: FiltersNameEnum.womenTShirt, label: castingFiltersLabel[FiltersNameEnum.womenTShirt] },
              { value: FiltersNameEnum.womenPants, label: castingFiltersLabel[FiltersNameEnum.womenPants] },
              { value: FiltersNameEnum.womenShoes, label: castingFiltersLabel[FiltersNameEnum.womenShoes] },
              { value: FiltersNameEnum.womenShoeWidth, label: castingFiltersLabel[FiltersNameEnum.womenShoeWidth] },
              { value: FiltersNameEnum.childrenShoes, label: castingFiltersLabel[FiltersNameEnum.childrenShoes] },
              {
                value: FiltersNameEnum.childrenShoeWidth,
                label: castingFiltersLabel[FiltersNameEnum.childrenShoeWidth],
              },
              { value: FiltersNameEnum.boys, label: castingFiltersLabel[FiltersNameEnum.boys] },
              { value: FiltersNameEnum.girls, label: castingFiltersLabel[FiltersNameEnum.girls] },
              { value: FiltersNameEnum.toddlers, label: castingFiltersLabel[FiltersNameEnum.toddlers] },
              { value: FiltersNameEnum.infants, label: castingFiltersLabel[FiltersNameEnum.infants] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.documents,
            label: castingFiltersLabel[FiltersNameEnum.documents],
            options: [
              {
                value: FiltersNameEnum.documents,
                label: castingFiltersLabel[FiltersNameEnum.documents],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.eyeColor,
            label: castingFiltersLabel[FiltersNameEnum.eyeColor],
            options: [
              {
                value: FiltersNameEnum.eyeColor,
                label: castingFiltersLabel[FiltersNameEnum.eyeColor],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.facialHair,
            label: castingFiltersLabel[FiltersNameEnum.facialHair],
            options: [
              {
                value: FiltersNameEnum.willingToFacialHair,
                label: castingFiltersLabel[FiltersNameEnum.willingToFacialHair],
              },
              {
                value: FiltersNameEnum.facialHair,
                label: castingFiltersLabel[FiltersNameEnum.facialHair],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.hairColor,
            label: castingFiltersLabel[FiltersNameEnum.hairColor],
            options: [
              {
                value: FiltersNameEnum.willingToHairColor,
                label: castingFiltersLabel[FiltersNameEnum.willingToHairColor],
              },
              {
                value: FiltersNameEnum.hairColor,
                label: castingFiltersLabel[FiltersNameEnum.hairColor],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.hairLengthStyle,
            label: castingFiltersLabel[FiltersNameEnum.hairStyle],
            options: [
              {
                value: FiltersNameEnum.willingToHairStyle,
                label: castingFiltersLabel[FiltersNameEnum.willingToHairStyle],
              },
              {
                value: FiltersNameEnum.hairStyle,
                label: castingFiltersLabel[FiltersNameEnum.hairStyle],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.multipleBirth,
            label: castingFiltersLabel[FiltersNameEnum.multipleBirth],
            options: [
              {
                value: FiltersNameEnum.multipleBirth,
                label: castingFiltersLabel[FiltersNameEnum.multipleBirth],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.otherExperience,
            label: castingFiltersLabel[FiltersNameEnum.otherExperience],
            options: [
              {
                value: FiltersNameEnum.otherExperience,
                label: castingFiltersLabel[FiltersNameEnum.otherExperience],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.newTalent,
            label: castingFiltersLabel[FiltersNameEnum.isNewTalent],
            options: [
              {
                value: FiltersNameEnum.isNewTalent,
                label: castingFiltersLabel[FiltersNameEnum.isNewTalent],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.pregnancyDueDate,
            label: castingFiltersLabel[FiltersNameEnum.pregnancyDueDateRange],
            options: [
              {
                value: FiltersNameEnum.pregnancyDueDateRange,
                label: castingFiltersLabel[FiltersNameEnum.pregnancyDueDateRange],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.profession,
            label: castingFiltersLabel[FiltersNameEnum.profession],
            options: [
              {
                value: FiltersNameEnum.profession,
                label: castingFiltersLabel[FiltersNameEnum.profession],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.rolePreferences,
            label: castingFiltersLabel[FiltersNameEnum.rolePreferences],
            options: [
              {
                value: FiltersNameEnum.rolePreferences,
                label: castingFiltersLabel[FiltersNameEnum.rolePreferences],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.skills,
            label: castingFiltersLabel[FiltersNameEnum.skills],
            options: [
              { value: FiltersNameEnum.skills, label: castingFiltersLabel[FiltersNameEnum.skills] },
              { value: FiltersNameEnum.skillLevels, label: castingFiltersLabel[FiltersNameEnum.skillLevels] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.tattoosAndPiercings,
            label: t('common:filtersName.tattoosAndPiercings'),
            options: [
              {
                value: FiltersNameEnum.tattoosList,
                label: castingFiltersLabel[FiltersNameEnum.tattoosList],
              },
              {
                value: FiltersNameEnum.tattoosNote,
                label: castingFiltersLabel[FiltersNameEnum.tattoosNote],
              },
              {
                value: FiltersNameEnum.piercingsList,
                label: castingFiltersLabel[FiltersNameEnum.piercingsList],
              },
              {
                value: FiltersNameEnum.piercingsNote,
                label: castingFiltersLabel[FiltersNameEnum.piercingsNote],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.uniqueAttributes,
            label: castingFiltersLabel[FiltersNameEnum.uniqueAttributes],
            options: [
              {
                value: FiltersNameEnum.uniqueAttributes,
                label: castingFiltersLabel[FiltersNameEnum.uniqueAttributes],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.vehicle,
            label: t('common:profile.assets.vehicle'),
            options: [
              { value: FiltersNameEnum.vehicleType, label: castingFiltersLabel[FiltersNameEnum.vehicleType] },
              {
                value: FiltersNameEnum.vehicleYearRange,
                label: castingFiltersLabel[FiltersNameEnum.vehicleYearRange],
              },
              { value: FiltersNameEnum.vehicleMake, label: castingFiltersLabel[FiltersNameEnum.vehicleMake] },
              { value: FiltersNameEnum.vehicleModel, label: castingFiltersLabel[FiltersNameEnum.vehicleModel] },
            ],
          },
          ...(isPrimaryWorkingLocationEnabled ? [workingLocationsFilter] : []),
        ],
      },
    ],
    [FilterDrawerType.Reps]: [
      {
        value: FiltersSectionNameEnum.topFilters,
        label: t('common:filtersName.topFilters'),
        options: [
          {
            value: FiltersCategoryNameEnum.profileStatus,
            label: repsFiltersLabel[FiltersNameEnum.profileStatus],
            options: [
              {
                value: FiltersNameEnum.profileStatus,
                label: repsFiltersLabel[FiltersNameEnum.profileStatus],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.unionStatus,
            label: repsFiltersLabel[FiltersNameEnum.unionStatus],
            options: [
              {
                value: FiltersNameEnum.unionStatus,
                label: repsFiltersLabel[FiltersNameEnum.unionStatus],
              },
              {
                value: FiltersNameEnum.excludeUnionStatus,
                label: repsFiltersLabel[FiltersNameEnum.unionStatus],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.genderAppearance,
            label: t('common:form.title.genderAppearance'),
            options: [
              { value: FiltersNameEnum.gender, label: repsFiltersLabel[FiltersNameEnum.gender] },
              {
                value: FiltersNameEnum.additionallyIdentifiesAs,
                label: repsFiltersLabel[FiltersNameEnum.additionallyIdentifiesAs],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.ethnicAppearance,
            label: repsFiltersLabel[FiltersNameEnum.ethnicAppearance],
            options: [
              {
                value: FiltersNameEnum.ethnicAppearance,
                label: repsFiltersLabel[FiltersNameEnum.additionallyIdentifiesAs],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.age,
            label: t('common:filtersName.age'),
            options: [
              { value: FiltersNameEnum.playableAgeRange, label: repsFiltersLabel[FiltersNameEnum.playableAgeRange] },
              { value: FiltersNameEnum.realAgeRange, label: repsFiltersLabel[FiltersNameEnum.realAgeRange] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.workingLocations,
            label: t('common:filtersName.workingLocations'),
            options: [
              {
                value: FiltersNameEnum.workingLocation,
                label: repsFiltersLabel[FiltersNameEnum.workingLocation],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.rosterLists,
            label: repsFiltersLabel[FiltersNameEnum.profileListIds],
            options: [
              {
                value: FiltersNameEnum.profileListIds,
                label: repsFiltersLabel[FiltersNameEnum.profileListIds],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.excludeTalentWithConflicts,
            label: repsFiltersLabel[FiltersNameEnum.excludeConflicts],
            options: [
              {
                value: FiltersNameEnum.excludeConflicts,
                label: repsFiltersLabel[FiltersNameEnum.excludeConflicts],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.bookedOutDates,
            label: repsFiltersLabel[FiltersNameEnum.excludeBookedOutDates],
            options: [
              {
                value: FiltersNameEnum.excludeBookedOutDates,
                label: repsFiltersLabel[FiltersNameEnum.excludeBookedOutDates],
              },
            ],
          },
        ],
      },
      {
        value: FiltersSectionNameEnum.additionalFilters,
        label: t('common:filtersName.additionalFilters'),
        options: [
          {
            value: FiltersCategoryNameEnum.assets,
            label: t('common:form.title.assets'),
            options: [
              { value: FiltersNameEnum.dogBreed, label: repsFiltersLabel[FiltersNameEnum.dogBreed] },
              { value: FiltersNameEnum.dogBreedSize, label: repsFiltersLabel[FiltersNameEnum.dogBreedSize] },
              { value: FiltersNameEnum.otherAnimals, label: repsFiltersLabel[FiltersNameEnum.otherAnimals] },
              { value: FiltersNameEnum.props, label: repsFiltersLabel[FiltersNameEnum.props] },
              { value: FiltersNameEnum.sportsEquipment, label: repsFiltersLabel[FiltersNameEnum.sportsEquipment] },
              { value: FiltersNameEnum.wardrobe, label: repsFiltersLabel[FiltersNameEnum.wardrobe] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.clothingSizes,
            label: t('common:profile.sizes.clothingSizes'),
            options: [
              { value: FiltersNameEnum.sizesCategory, label: repsFiltersLabel[FiltersNameEnum.sizesCategory] },
              {
                value: FiltersNameEnum.sizesChildrenCategory,
                label: repsFiltersLabel[FiltersNameEnum.sizesChildrenCategory],
              },
              { value: FiltersNameEnum.chest, label: repsFiltersLabel[FiltersNameEnum.chest] },
              { value: FiltersNameEnum.neck, label: repsFiltersLabel[FiltersNameEnum.neck] },
              { value: FiltersNameEnum.sleeve, label: repsFiltersLabel[FiltersNameEnum.sleeve] },
              { value: FiltersNameEnum.coat, label: repsFiltersLabel[FiltersNameEnum.coat] },
              { value: FiltersNameEnum.waist, label: repsFiltersLabel[FiltersNameEnum.waist] },
              { value: FiltersNameEnum.inseam, label: repsFiltersLabel[FiltersNameEnum.inseam] },
              { value: FiltersNameEnum.glove, label: repsFiltersLabel[FiltersNameEnum.glove] },
              { value: FiltersNameEnum.hat, label: repsFiltersLabel[FiltersNameEnum.hat] },
              { value: FiltersNameEnum.menTShirt, label: repsFiltersLabel[FiltersNameEnum.menTShirt] },
              { value: FiltersNameEnum.menShoes, label: repsFiltersLabel[FiltersNameEnum.menShoes] },
              { value: FiltersNameEnum.menShoeWidth, label: repsFiltersLabel[FiltersNameEnum.menShoeWidth] },
              { value: FiltersNameEnum.dress, label: repsFiltersLabel[FiltersNameEnum.dress] },
              { value: FiltersNameEnum.bust, label: repsFiltersLabel[FiltersNameEnum.bust] },
              { value: FiltersNameEnum.underbust, label: repsFiltersLabel[FiltersNameEnum.underbust] },
              { value: FiltersNameEnum.cup, label: repsFiltersLabel[FiltersNameEnum.cup] },
              { value: FiltersNameEnum.hip, label: repsFiltersLabel[FiltersNameEnum.hip] },
              { value: FiltersNameEnum.womenTShirt, label: repsFiltersLabel[FiltersNameEnum.womenTShirt] },
              { value: FiltersNameEnum.womenPants, label: repsFiltersLabel[FiltersNameEnum.womenPants] },
              { value: FiltersNameEnum.womenShoes, label: repsFiltersLabel[FiltersNameEnum.womenShoes] },
              { value: FiltersNameEnum.womenShoeWidth, label: repsFiltersLabel[FiltersNameEnum.womenShoeWidth] },
              { value: FiltersNameEnum.childrenShoes, label: repsFiltersLabel[FiltersNameEnum.childrenShoes] },
              {
                value: FiltersNameEnum.childrenShoeWidth,
                label: repsFiltersLabel[FiltersNameEnum.childrenShoeWidth],
              },
              { value: FiltersNameEnum.boys, label: repsFiltersLabel[FiltersNameEnum.boys] },
              { value: FiltersNameEnum.girls, label: repsFiltersLabel[FiltersNameEnum.girls] },
              { value: FiltersNameEnum.toddlers, label: repsFiltersLabel[FiltersNameEnum.toddlers] },
              { value: FiltersNameEnum.infants, label: repsFiltersLabel[FiltersNameEnum.infants] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.documents,
            label: repsFiltersLabel[FiltersNameEnum.documents],
            options: [
              {
                value: FiltersNameEnum.documents,
                label: repsFiltersLabel[FiltersNameEnum.documents],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.eyeColor,
            label: repsFiltersLabel[FiltersNameEnum.eyeColor],
            options: [
              {
                value: FiltersNameEnum.eyeColor,
                label: repsFiltersLabel[FiltersNameEnum.eyeColor],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.facialHair,
            label: repsFiltersLabel[FiltersNameEnum.facialHair],
            options: [
              {
                value: FiltersNameEnum.willingToFacialHair,
                label: repsFiltersLabel[FiltersNameEnum.willingToFacialHair],
              },
              {
                value: FiltersNameEnum.facialHair,
                label: repsFiltersLabel[FiltersNameEnum.facialHair],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.hairColor,
            label: repsFiltersLabel[FiltersNameEnum.hairColor],
            options: [
              {
                value: FiltersNameEnum.willingToHairColor,
                label: repsFiltersLabel[FiltersNameEnum.willingToHairColor],
              },
              {
                value: FiltersNameEnum.hairColor,
                label: repsFiltersLabel[FiltersNameEnum.hairColor],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.hairLengthStyle,
            label: repsFiltersLabel[FiltersNameEnum.hairStyle],
            options: [
              {
                value: FiltersNameEnum.willingToHairStyle,
                label: repsFiltersLabel[FiltersNameEnum.willingToHairStyle],
              },
              {
                value: FiltersNameEnum.hairStyle,
                label: repsFiltersLabel[FiltersNameEnum.hairStyle],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.heightAndWeight,
            label: t('common:form.title.heightAndWeight'),
            options: [
              { value: FiltersNameEnum.heightRange, label: repsFiltersLabel[FiltersNameEnum.heightRange] },
              { value: FiltersNameEnum.weightRange, label: repsFiltersLabel[FiltersNameEnum.weightRange] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.multipleBirth,
            label: repsFiltersLabel[FiltersNameEnum.multipleBirth],
            options: [
              {
                value: FiltersNameEnum.multipleBirth,
                label: repsFiltersLabel[FiltersNameEnum.multipleBirth],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.otherExperience,
            label: castingFiltersLabel[FiltersNameEnum.otherExperience],
            options: [
              {
                value: FiltersNameEnum.otherExperience,
                label: castingFiltersLabel[FiltersNameEnum.otherExperience],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.pregnancyDueDate,
            label: repsFiltersLabel[FiltersNameEnum.pregnancyDueDateRange],
            options: [
              {
                value: FiltersNameEnum.pregnancyDueDateRange,
                label: repsFiltersLabel[FiltersNameEnum.pregnancyDueDateRange],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.profession,
            label: repsFiltersLabel[FiltersNameEnum.profession],
            options: [
              {
                value: FiltersNameEnum.profession,
                label: repsFiltersLabel[FiltersNameEnum.profession],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.rolePreferences,
            label: repsFiltersLabel[FiltersNameEnum.rolePreferences],
            options: [
              {
                value: FiltersNameEnum.rolePreferences,
                label: repsFiltersLabel[FiltersNameEnum.rolePreferences],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.skills,
            label: repsFiltersLabel[FiltersNameEnum.skills],
            options: [
              { value: FiltersNameEnum.skills, label: repsFiltersLabel[FiltersNameEnum.skills] },
              { value: FiltersNameEnum.skillLevels, label: repsFiltersLabel[FiltersNameEnum.skillLevels] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.tattoosAndPiercings,
            label: t('common:filtersName.tattoosAndPiercings'),
            options: [
              {
                value: FiltersNameEnum.tattoosList,
                label: repsFiltersLabel[FiltersNameEnum.tattoosList],
              },
              {
                value: FiltersNameEnum.tattoosNote,
                label: repsFiltersLabel[FiltersNameEnum.tattoosNote],
              },
              {
                value: FiltersNameEnum.piercingsList,
                label: repsFiltersLabel[FiltersNameEnum.piercingsList],
              },
              {
                value: FiltersNameEnum.piercingsNote,
                label: repsFiltersLabel[FiltersNameEnum.piercingsNote],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.uniqueAttributes,
            label: repsFiltersLabel[FiltersNameEnum.uniqueAttributes],
            options: [
              {
                value: FiltersNameEnum.uniqueAttributes,
                label: repsFiltersLabel[FiltersNameEnum.uniqueAttributes],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.vehicle,
            label: t('common:profile.assets.vehicle'),
            options: [
              { value: FiltersNameEnum.vehicleType, label: repsFiltersLabel[FiltersNameEnum.vehicleType] },
              { value: FiltersNameEnum.vehicleYearRange, label: repsFiltersLabel[FiltersNameEnum.vehicleYearRange] },
              { value: FiltersNameEnum.vehicleMake, label: repsFiltersLabel[FiltersNameEnum.vehicleMake] },
              { value: FiltersNameEnum.vehicleModel, label: repsFiltersLabel[FiltersNameEnum.vehicleModel] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.workPermitExpirationDates,
            label: repsFiltersLabel[FiltersNameEnum.workPermitExpiration],
            options: [
              {
                value: FiltersNameEnum.workPermitExpiration,
                label: repsFiltersLabel[FiltersNameEnum.workPermitExpiration],
              },
            ],
          },
        ],
      },
    ],
    [FilterDrawerType.TalentScout]: [
      {
        value: FiltersSectionNameEnum.topFilters,
        label: t('common:filtersName.topFilters'),
        options: [
          {
            value: FiltersCategoryNameEnum.seekingRepresentation,
            label: talentScoutFiltersLabel[FiltersNameEnum.seekingRepresentation],
            options: [
              {
                value: FiltersNameEnum.seekingRepresentation,
                label: talentScoutFiltersLabel[FiltersNameEnum.seekingRepresentation],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.unionStatus,
            label: talentScoutFiltersLabel[FiltersNameEnum.unionStatus],
            options: [
              {
                value: FiltersNameEnum.unionStatus,
                label: talentScoutFiltersLabel[FiltersNameEnum.unionStatus],
              },
              {
                value: FiltersNameEnum.excludeUnionStatus,
                label: talentScoutFiltersLabel[FiltersNameEnum.unionStatus],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.genderAppearance,
            label: t('common:form.title.genderAppearance'),
            options: [
              { value: FiltersNameEnum.gender, label: talentScoutFiltersLabel[FiltersNameEnum.gender] },
              {
                value: FiltersNameEnum.additionallyIdentifiesAs,
                label: talentScoutFiltersLabel[FiltersNameEnum.additionallyIdentifiesAs],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.ethnicAppearance,
            label: talentScoutFiltersLabel[FiltersNameEnum.ethnicAppearance],
            options: [
              {
                value: FiltersNameEnum.ethnicAppearance,
                label: talentScoutFiltersLabel[FiltersNameEnum.additionallyIdentifiesAs],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.age,
            label: t('common:filtersName.age'),
            options: [
              {
                value: FiltersNameEnum.playableAgeRange,
                label: talentScoutFiltersLabel[FiltersNameEnum.playableAgeRange],
              },
              { value: FiltersNameEnum.minorsAgeRange, label: talentScoutFiltersLabel[FiltersNameEnum.minorsAgeRange] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.workingLocations,
            label: t('common:filtersName.workingLocations'),
            options: [
              {
                value: FiltersNameEnum.workingLocation,
                label: repsFiltersLabel[FiltersNameEnum.workingLocation],
              },
            ],
          },
        ],
      },
      {
        value: FiltersSectionNameEnum.additionalFilters,
        label: t('common:filtersName.additionalFilters'),
        options: [
          {
            value: FiltersCategoryNameEnum.assets,
            label: t('common:form.title.assets'),
            options: [
              { value: FiltersNameEnum.dogBreed, label: talentScoutFiltersLabel[FiltersNameEnum.dogBreed] },
              { value: FiltersNameEnum.dogBreedSize, label: talentScoutFiltersLabel[FiltersNameEnum.dogBreedSize] },
              { value: FiltersNameEnum.otherAnimals, label: talentScoutFiltersLabel[FiltersNameEnum.otherAnimals] },
              { value: FiltersNameEnum.props, label: talentScoutFiltersLabel[FiltersNameEnum.props] },
              {
                value: FiltersNameEnum.sportsEquipment,
                label: talentScoutFiltersLabel[FiltersNameEnum.sportsEquipment],
              },
              { value: FiltersNameEnum.wardrobe, label: talentScoutFiltersLabel[FiltersNameEnum.wardrobe] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.clothingSizes,
            label: t('common:profile.sizes.clothingSizes'),
            options: [
              { value: FiltersNameEnum.sizesCategory, label: talentScoutFiltersLabel[FiltersNameEnum.sizesCategory] },
              {
                value: FiltersNameEnum.sizesChildrenCategory,
                label: talentScoutFiltersLabel[FiltersNameEnum.sizesChildrenCategory],
              },
              { value: FiltersNameEnum.chest, label: talentScoutFiltersLabel[FiltersNameEnum.chest] },
              { value: FiltersNameEnum.neck, label: talentScoutFiltersLabel[FiltersNameEnum.neck] },
              { value: FiltersNameEnum.sleeve, label: talentScoutFiltersLabel[FiltersNameEnum.sleeve] },
              { value: FiltersNameEnum.coat, label: talentScoutFiltersLabel[FiltersNameEnum.coat] },
              { value: FiltersNameEnum.waist, label: talentScoutFiltersLabel[FiltersNameEnum.waist] },
              { value: FiltersNameEnum.inseam, label: talentScoutFiltersLabel[FiltersNameEnum.inseam] },
              { value: FiltersNameEnum.glove, label: talentScoutFiltersLabel[FiltersNameEnum.glove] },
              { value: FiltersNameEnum.hat, label: talentScoutFiltersLabel[FiltersNameEnum.hat] },
              { value: FiltersNameEnum.menTShirt, label: talentScoutFiltersLabel[FiltersNameEnum.menTShirt] },
              { value: FiltersNameEnum.menShoes, label: talentScoutFiltersLabel[FiltersNameEnum.menShoes] },
              { value: FiltersNameEnum.menShoeWidth, label: talentScoutFiltersLabel[FiltersNameEnum.menShoeWidth] },
              { value: FiltersNameEnum.dress, label: talentScoutFiltersLabel[FiltersNameEnum.dress] },
              { value: FiltersNameEnum.bust, label: talentScoutFiltersLabel[FiltersNameEnum.bust] },
              { value: FiltersNameEnum.underbust, label: talentScoutFiltersLabel[FiltersNameEnum.underbust] },
              { value: FiltersNameEnum.cup, label: talentScoutFiltersLabel[FiltersNameEnum.cup] },
              { value: FiltersNameEnum.hip, label: talentScoutFiltersLabel[FiltersNameEnum.hip] },
              { value: FiltersNameEnum.womenTShirt, label: talentScoutFiltersLabel[FiltersNameEnum.womenTShirt] },
              { value: FiltersNameEnum.womenPants, label: talentScoutFiltersLabel[FiltersNameEnum.womenPants] },
              { value: FiltersNameEnum.womenShoes, label: talentScoutFiltersLabel[FiltersNameEnum.womenShoes] },
              { value: FiltersNameEnum.womenShoeWidth, label: talentScoutFiltersLabel[FiltersNameEnum.womenShoeWidth] },
              { value: FiltersNameEnum.childrenShoes, label: talentScoutFiltersLabel[FiltersNameEnum.childrenShoes] },
              {
                value: FiltersNameEnum.childrenShoeWidth,
                label: talentScoutFiltersLabel[FiltersNameEnum.childrenShoeWidth],
              },
              { value: FiltersNameEnum.boys, label: talentScoutFiltersLabel[FiltersNameEnum.boys] },
              { value: FiltersNameEnum.girls, label: talentScoutFiltersLabel[FiltersNameEnum.girls] },
              { value: FiltersNameEnum.toddlers, label: talentScoutFiltersLabel[FiltersNameEnum.toddlers] },
              { value: FiltersNameEnum.infants, label: talentScoutFiltersLabel[FiltersNameEnum.infants] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.documents,
            label: talentScoutFiltersLabel[FiltersNameEnum.documents],
            options: [
              {
                value: FiltersNameEnum.documents,
                label: talentScoutFiltersLabel[FiltersNameEnum.documents],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.eyeColor,
            label: talentScoutFiltersLabel[FiltersNameEnum.eyeColor],
            options: [
              {
                value: FiltersNameEnum.eyeColor,
                label: talentScoutFiltersLabel[FiltersNameEnum.eyeColor],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.facialHair,
            label: talentScoutFiltersLabel[FiltersNameEnum.facialHair],
            options: [
              {
                value: FiltersNameEnum.willingToFacialHair,
                label: talentScoutFiltersLabel[FiltersNameEnum.willingToFacialHair],
              },
              {
                value: FiltersNameEnum.facialHair,
                label: talentScoutFiltersLabel[FiltersNameEnum.facialHair],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.hairColor,
            label: talentScoutFiltersLabel[FiltersNameEnum.hairColor],
            options: [
              {
                value: FiltersNameEnum.willingToHairColor,
                label: talentScoutFiltersLabel[FiltersNameEnum.willingToHairColor],
              },
              {
                value: FiltersNameEnum.hairColor,
                label: talentScoutFiltersLabel[FiltersNameEnum.hairColor],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.hairLengthStyle,
            label: talentScoutFiltersLabel[FiltersNameEnum.hairStyle],
            options: [
              {
                value: FiltersNameEnum.willingToHairStyle,
                label: talentScoutFiltersLabel[FiltersNameEnum.willingToHairStyle],
              },
              {
                value: FiltersNameEnum.hairStyle,
                label: talentScoutFiltersLabel[FiltersNameEnum.hairStyle],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.heightAndWeight,
            label: t('common:form.title.heightAndWeight'),
            options: [
              { value: FiltersNameEnum.heightRange, label: repsFiltersLabel[FiltersNameEnum.heightRange] },
              { value: FiltersNameEnum.weightRange, label: repsFiltersLabel[FiltersNameEnum.weightRange] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.multipleBirth,
            label: talentScoutFiltersLabel[FiltersNameEnum.multipleBirth],
            options: [
              {
                value: FiltersNameEnum.multipleBirth,
                label: talentScoutFiltersLabel[FiltersNameEnum.multipleBirth],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.otherExperience,
            label: talentScoutFiltersLabel[FiltersNameEnum.otherExperience],
            options: [
              {
                value: FiltersNameEnum.otherExperience,
                label: talentScoutFiltersLabel[FiltersNameEnum.otherExperience],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.pregnancyDueDate,
            label: talentScoutFiltersLabel[FiltersNameEnum.pregnancyDueDateRange],
            options: [
              {
                value: FiltersNameEnum.pregnancyDueDateRange,
                label: talentScoutFiltersLabel[FiltersNameEnum.pregnancyDueDateRange],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.profession,
            label: talentScoutFiltersLabel[FiltersNameEnum.profession],
            options: [
              {
                value: FiltersNameEnum.profession,
                label: talentScoutFiltersLabel[FiltersNameEnum.profession],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.rolePreferences,
            label: talentScoutFiltersLabel[FiltersNameEnum.rolePreferences],
            options: [
              {
                value: FiltersNameEnum.rolePreferences,
                label: talentScoutFiltersLabel[FiltersNameEnum.rolePreferences],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.skills,
            label: talentScoutFiltersLabel[FiltersNameEnum.skills],
            options: [
              { value: FiltersNameEnum.skills, label: talentScoutFiltersLabel[FiltersNameEnum.skills] },
              { value: FiltersNameEnum.skillLevels, label: talentScoutFiltersLabel[FiltersNameEnum.skillLevels] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.tattoosAndPiercings,
            label: t('common:filtersName.tattoosAndPiercings'),
            options: [
              {
                value: FiltersNameEnum.tattoosList,
                label: talentScoutFiltersLabel[FiltersNameEnum.tattoosList],
              },
              {
                value: FiltersNameEnum.tattoosNote,
                label: talentScoutFiltersLabel[FiltersNameEnum.tattoosNote],
              },
              {
                value: FiltersNameEnum.piercingsList,
                label: talentScoutFiltersLabel[FiltersNameEnum.piercingsList],
              },
              {
                value: FiltersNameEnum.piercingsNote,
                label: talentScoutFiltersLabel[FiltersNameEnum.piercingsNote],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.uniqueAttributes,
            label: talentScoutFiltersLabel[FiltersNameEnum.uniqueAttributes],
            options: [
              {
                value: FiltersNameEnum.uniqueAttributes,
                label: talentScoutFiltersLabel[FiltersNameEnum.uniqueAttributes],
              },
            ],
          },
          {
            value: FiltersCategoryNameEnum.vehicle,
            label: t('common:profile.assets.vehicle'),
            options: [
              { value: FiltersNameEnum.vehicleType, label: talentScoutFiltersLabel[FiltersNameEnum.vehicleType] },
              {
                value: FiltersNameEnum.vehicleYearRange,
                label: talentScoutFiltersLabel[FiltersNameEnum.vehicleYearRange],
              },
              { value: FiltersNameEnum.vehicleMake, label: talentScoutFiltersLabel[FiltersNameEnum.vehicleMake] },
              { value: FiltersNameEnum.vehicleModel, label: talentScoutFiltersLabel[FiltersNameEnum.vehicleModel] },
            ],
          },
          {
            value: FiltersCategoryNameEnum.workPermitExpirationDates,
            label: repsFiltersLabel[FiltersNameEnum.workPermitExpiration],
            options: [
              {
                value: FiltersNameEnum.workPermitExpiration,
                label: repsFiltersLabel[FiltersNameEnum.workPermitExpiration],
              },
            ],
          },
        ],
      },
    ],
  };

  return drawers[type];
};
